/**
 * Website Configuration Page
 *
 * Displays the list of CyberSweep websites grouped by Website Category.
 * Attempts to fetch from Gobodo/RDS (Audit_Website_Configuration__c object).
 * Falls back to static data if Gobodo fails or returns empty.
 * Groups websites by Website_Category__c field.
 */

import React, { useState, useMemo } from 'react';
import { Globe, CheckCircle, XCircle, Search, Loader2, AlertTriangle } from 'lucide-react';
import { WEBSITE_CONFIGURATIONS, WEBSITE_CATEGORIES, WebsiteConfiguration, ChecklistType, getWebsitesByChecklistType } from '../../data/websiteConfigurations';
import { StandardSearchBar } from '../../components/standard';
import { useWebsiteConfigurations, GobodoWebsiteConfig } from '../../services/queries/cyberSweepQueries';

type ChecklistFilter = 'total' | 'active' | 'initial_deletion' | 'comprehensive' | 'minimal';

interface WebsiteConfigurationPageProps {
  onBack?: () => void;
  onWebsiteClick?: (websiteId: string) => void;
}

// Unified website type that can come from either source
interface DisplayWebsite {
  id: string;
  name: string;
  category: string;
  isActive: boolean;
  checklistTypes: ChecklistType[];
}

// Convert Gobodo website to display format
function gobodoToDisplay(website: GobodoWebsiteConfig): DisplayWebsite {
  const gobodoName = website.name.toLowerCase().trim();

  // Try to match with static data for checklist types
  // First try exact match, then try partial/fuzzy matching
  let staticMatch = WEBSITE_CONFIGURATIONS.find(
    w => w.websiteCompany.toLowerCase().trim() === gobodoName
  );

  // If no exact match, try if gobodo name contains or is contained by static name
  if (!staticMatch) {
    staticMatch = WEBSITE_CONFIGURATIONS.find(w => {
      const staticName = w.websiteCompany.toLowerCase().trim();
      return gobodoName.includes(staticName) || staticName.includes(gobodoName);
    });
  }

  return {
    id: website.id,
    name: website.name,
    category: website.websiteCategory || 'Uncategorized',
    isActive: website.isActive,
    checklistTypes: staticMatch?.checklistTypes || [],
  };
}

// Convert static website to display format
function staticToDisplay(website: WebsiteConfiguration): DisplayWebsite {
  const category = WEBSITE_CATEGORIES.find(c => c.code === website.category);
  return {
    id: website.id,
    name: website.websiteCompany,
    category: category?.name || website.category,
    isActive: website.isActive,
    checklistTypes: website.checklistTypes,
  };
}

const WebsiteConfigurationPage: React.FC<WebsiteConfigurationPageProps> = ({ onBack, onWebsiteClick }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [checklistFilter, setChecklistFilter] = useState<ChecklistFilter>('total');

  // Try to fetch from Gobodo/RDS
  const { data: gobodoWebsites, isLoading, error } = useWebsiteConfigurations();

  // Log for debugging
  if (error) {
    console.error('[WebsiteConfigurationPage] Gobodo error:', error);
  }
  if (gobodoWebsites) {
    console.log('[WebsiteConfigurationPage] Gobodo returned', gobodoWebsites.length, 'websites');
  }

  // Determine data source: Gobodo if available and has data, otherwise static
  const useGobodo = !error && gobodoWebsites && gobodoWebsites.length > 0;

  // Convert to unified display format
  const websites: DisplayWebsite[] = useMemo(() => {
    if (useGobodo && gobodoWebsites) {
      return gobodoWebsites.map(gobodoToDisplay);
    }
    return WEBSITE_CONFIGURATIONS.map(staticToDisplay);
  }, [useGobodo, gobodoWebsites]);

  // Get unique categories from data
  const categories = useMemo(() => {
    const categorySet = new Set<string>();
    websites.forEach(w => {
      if (w.category) {
        categorySet.add(w.category);
      }
    });
    return Array.from(categorySet).sort();
  }, [websites]);

  // Compute counts for stat cards
  // Total and Active come from actual data source (Gobodo or static)
  // Checklist type counts always use static data until RDS field is added
  const counts = useMemo(() => {
    const total = useGobodo ? websites.length : WEBSITE_CONFIGURATIONS.length;
    const active = useGobodo
      ? websites.filter(w => w.isActive).length
      : WEBSITE_CONFIGURATIONS.filter(w => w.isActive).length;

    // Always use static counts for checklist types (future: add field to RDS)
    return {
      total,
      active,
      initialDeletion: getWebsitesByChecklistType('initial_deletion').length,
      comprehensive: getWebsitesByChecklistType('comprehensive').length,
      minimal: getWebsitesByChecklistType('minimal').length,
    };
  }, [websites, useGobodo]);

  // Filter websites based on search, category, and checklist type
  const filteredWebsites = useMemo(() => websites.filter(website => {
    const matchesSearch = !searchQuery ||
      website.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || website.category === selectedCategory;

    let matchesChecklist = true;
    if (checklistFilter === 'active') {
      matchesChecklist = website.isActive;
    } else if (checklistFilter !== 'total') {
      matchesChecklist = website.checklistTypes.includes(checklistFilter);
    }

    return matchesSearch && matchesCategory && matchesChecklist;
  }), [websites, searchQuery, selectedCategory, checklistFilter]);

  // Group filtered websites by category
  const groupedWebsites = useMemo(() => {
    const groups: Record<string, DisplayWebsite[]> = {};

    filteredWebsites.forEach(website => {
      const category = website.category || 'Uncategorized';
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(website);
    });

    // Sort websites within each category by name
    Object.keys(groups).forEach(cat => {
      groups[cat].sort((a, b) => a.name.localeCompare(b.name));
    });

    return groups;
  }, [filteredWebsites]);

  // Get sorted category names for display
  const sortedCategories = useMemo(() => {
    return Object.keys(groupedWebsites).sort();
  }, [groupedWebsites]);

  return (
    <div className="space-y-3">
      {/* Header - Compact */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Globe className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Website Configuration</h2>
          <span className="text-sm text-gray-500 dark:text-gray-400">({counts.total} websites)</span>
          {isLoading && (
            <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
          )}
        </div>
      </div>

      {/* Warning if using fallback due to error */}
      {error && (
        <div className="flex items-center gap-2 text-xs text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/20 px-3 py-1.5 rounded">
          <AlertTriangle className="h-3.5 w-3.5" />
          <span>Using static data - Gobodo returned an error</span>
        </div>
      )}

      {/* Stats Cards - Clickable filters */}
      <div className="grid grid-cols-5 gap-2">
        {/* Total */}
        <button
          onClick={() => setChecklistFilter('total')}
          className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm px-3 py-2 text-left transition-all ${
            checklistFilter === 'total' ? 'ring-2 ring-gray-400 dark:ring-gray-500' : 'hover:shadow-md'
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500 dark:text-gray-400">Total</span>
            <Globe className="h-3.5 w-3.5 text-gray-400" />
          </div>
          <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{counts.total}</p>
        </button>

        {/* Active */}
        <button
          onClick={() => setChecklistFilter('active')}
          className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm px-3 py-2 text-left transition-all ${
            checklistFilter === 'active' ? 'ring-2 ring-green-400 dark:ring-green-500' : 'hover:shadow-md'
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500 dark:text-gray-400">Active</span>
            <CheckCircle className="h-3.5 w-3.5 text-green-500" />
          </div>
          <p className="text-lg font-bold text-green-600 dark:text-green-400">{counts.active}</p>
        </button>

        {/* Initial Deletion */}
        <button
          onClick={() => setChecklistFilter('initial_deletion')}
          className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm px-3 py-2 text-left transition-all ${
            checklistFilter === 'initial_deletion' ? 'ring-2 ring-purple-400 dark:ring-purple-500' : 'hover:shadow-md'
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500 dark:text-gray-400">Initial Deletion</span>
            <Globe className="h-3.5 w-3.5 text-purple-500" />
          </div>
          <p className="text-lg font-bold text-purple-600 dark:text-purple-400">{counts.initialDeletion}</p>
        </button>

        {/* Comprehensive */}
        <button
          onClick={() => setChecklistFilter('comprehensive')}
          className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm px-3 py-2 text-left transition-all ${
            checklistFilter === 'comprehensive' ? 'ring-2 ring-blue-400 dark:ring-blue-500' : 'hover:shadow-md'
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500 dark:text-gray-400">Comprehensive</span>
            <Globe className="h-3.5 w-3.5 text-blue-500" />
          </div>
          <p className="text-lg font-bold text-blue-600 dark:text-blue-400">{counts.comprehensive}</p>
        </button>

        {/* Minimal */}
        <button
          onClick={() => setChecklistFilter('minimal')}
          className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm px-3 py-2 text-left transition-all ${
            checklistFilter === 'minimal' ? 'ring-2 ring-green-400 dark:ring-green-500' : 'hover:shadow-md'
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500 dark:text-gray-400">Minimal</span>
            <Globe className="h-3.5 w-3.5 text-green-500" />
          </div>
          <p className="text-lg font-bold text-green-600 dark:text-green-400">{counts.minimal}</p>
        </button>
      </div>

      {/* Search and Filter */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm px-3 py-2">
        <div className="flex items-center gap-3">
          <div className="flex-1 min-w-[180px]">
            <StandardSearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search websites..."
              size="sm"
            />
          </div>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          >
            <option value="all">All Categories</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Websites by Category */}
      <div className="space-y-3">
        {sortedCategories.map(categoryName => {
          const categoryWebsites = groupedWebsites[categoryName];
          if (!categoryWebsites || categoryWebsites.length === 0) return null;

          return (
            <div key={categoryName} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
              <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      {categoryName}
                    </h3>
                  </div>
                  <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 rounded-full">
                    {categoryWebsites.length}
                  </span>
                </div>
              </div>
              <div className="divide-y divide-gray-200 dark:divide-gray-700 max-h-[300px] overflow-y-auto">
                {categoryWebsites.map(website => (
                  <div
                    key={website.id}
                    onClick={() => onWebsiteClick?.(website.id)}
                    className={`px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
                      onWebsiteClick ? 'cursor-pointer' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Globe className="h-4 w-4 text-gray-400" />
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {website.name}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        {website.checklistTypes.includes('initial_deletion') && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300">
                            Init
                          </span>
                        )}
                        {website.checklistTypes.includes('comprehensive') && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
                            Comp
                          </span>
                        )}
                        {website.checklistTypes.includes('minimal') && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300">
                            Min
                          </span>
                        )}
                        {website.isActive ? (
                          <CheckCircle className="h-4 w-4 text-green-500" />
                        ) : (
                          <XCircle className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Empty state */}
      {filteredWebsites.length === 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm px-4 py-8 text-center">
          <Search className="h-8 w-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No websites match your search
          </p>
        </div>
      )}
    </div>
  );
};

export default WebsiteConfigurationPage;
